import { useEffect, useMemo, useRef, startTransition } from 'react';
import {
  onSnapshot, query, where, limit, updateDoc,
} from 'firebase/firestore';
import {
  collectionRef, docRef, studentSubCol,
} from '../../firebase/config';
import { generateUserCode } from '../../utils/stringUtils';
import { INSTRUCTOR_STATUS, COLLECTIONS } from '../../utils/constants';
import { filterMyStudents, dedupeStudentsByEmailPreferUid, sameProgramList } from '../../utils/studentLogic';
import { emailsMatch } from '../../utils/emailUtils';
import { resolveAuthIdentity } from '../../utils/authIdentity';
import { subscribeStudentSubData } from './studentSubDataListeners';

const NOTIFICATION_LIMIT = 50;

const mapAndSortNotifications = (docs) =>
  docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
    .slice(0, NOTIFICATION_LIMIT);

const listenerErrorMessage = (err, label) => {
  const code = err?.code || '';
  if (code === 'permission-denied') return `${label} şu an görüntülenemiyor.`;
  if (code === 'failed-precondition') return `${label} henüz hazır değil. Biraz sonra deneyin.`;
  if (code === 'unavailable') return `${label} için bağlantı sorunu oluştu.`;
  return `${label} yüklenemedi.`;
};

const profileFieldsChanged = (prev, next) => {
  if (!prev || !next) return true;
  const keys = [
    'name', 'status', 'instructorEmail', 'instructorId',
    'userCode', 'age', 'parent',
  ];
  return keys.some((k) => prev[k] !== next[k]);
};

/**
 * Firestore gerçek zamanlı dinleyicileri.
 * Koleksiyon sorguları mümkün olduğunca auth.uid ile yapılır (Firestore OR kuralları).
 */
export const useCanliVeri = ({
  firebaseUser,
  loggedInUser,
  selectedStudent,
  students,
  showToast,
  setInstructors,
  setStudents,
  setNotifications,
  setMySubData,
  setAllStudentsPrograms,
  setSelectedStudentSubData,
  setLoggedInUser,
  setLiveDataReady,
}) => {
  const backfillAttemptedRef = useRef(new Set());

  useEffect(() => {
    if (!firebaseUser || !loggedInUser) return undefined;

    let cancelled = false;
    const unsubscribers = [];

    const showListenerErrorOnce = (() => {
      const shown = new Set();
      return (key, err, label) => {
        if (shown.has(key)) return;
        shown.add(key);
        console.error(`${label}:`, err);
        showToast(listenerErrorMessage(err, label), 'error');
      };
    })();

    const attachListeners = async () => {
      const { uid: authUid, loginId: authEmail } = await resolveAuthIdentity(firebaseUser);
      if (cancelled || !authUid) return;

      const pushListener = (subscribe) => {
        if (cancelled) return;
        const unsub = subscribe();
        if (cancelled) {
          unsub();
          return;
        }
        unsubscribers.push(unsub);
      };

      backfillAttemptedRef.current = new Set();

      const readyFlags = { instructors: false, students: false };
      const checkLiveReady = () => {
        if (readyFlags.instructors && readyFlags.students) {
          setLiveDataReady?.(true);
        }
      };

      const markReadyOnError = (flag) => {
        readyFlags[flag] = true;
        checkLiveReady();
      };

      const maybeBackfillUserCode = (collectionName, docId, data) => {
        if (data.userCode) return data;
        const backfillKey = `${collectionName}/${docId}`;
        if (backfillAttemptedRef.current.has(backfillKey)) return data;
        backfillAttemptedRef.current.add(backfillKey);

        const generated = generateUserCode();
        const canWrite =
          (collectionName === COLLECTIONS.INSTRUCTORS &&
            loggedInUser.role === 'instructor' &&
            docId === authUid) ||
          (collectionName === COLLECTIONS.STUDENTS &&
            loggedInUser.role === 'student' &&
            docId === authUid) ||
          (collectionName === COLLECTIONS.STUDENTS &&
            loggedInUser.role === 'instructor' &&
            (data.instructorId === authUid || emailsMatch(data.instructorEmail, authEmail)));

        if (canWrite) {
          updateDoc(docRef(collectionName, docId), { userCode: generated }).catch((e) =>
            console.warn(`${collectionName} userCode backfill:`, e)
          );
        }
        return { ...data, userCode: generated };
      };

      const syncInstructorProfile = (profileData) => {
        setLoggedInUser((prev) => {
          const next = {
            ...prev,
            ...profileData,
            id: authUid,
            emailOrPhone: authEmail,
            role: 'instructor',
          };
          return profileFieldsChanged(prev, next) ? next : prev;
        });
      };

      const syncStudentProfile = (profileData) => {
        setLoggedInUser((prev) => {
          const next = {
            ...prev,
            ...profileData,
            id: authUid,
            emailOrPhone: authEmail,
            role: 'student',
          };
          return profileFieldsChanged(prev, next) ? next : prev;
        });
      };

      // Bildirimler — recipientUid (kurallarla birebir)
      pushListener(() =>
        onSnapshot(
          query(collectionRef(COLLECTIONS.NOTIFICATIONS), where('recipientUid', '==', authUid)),
          (snapshot) => {
            startTransition(() => {
              setNotifications(mapAndSortNotifications(snapshot.docs));
            });
          },
          (err) => {
            showListenerErrorOnce('notifications', err, 'Bildirimler');
            startTransition(() => setNotifications([]));
          }
        )
      );

      if (loggedInUser.role === 'student') {
        pushListener(() =>
          onSnapshot(
            query(
              collectionRef('instructors'),
              where('status', '==', INSTRUCTOR_STATUS.ACTIVE),
              limit(500)
            ),
            (snapshot) => {
              readyFlags.instructors = true;
              checkLiveReady();
              const fetchedList = snapshot.docs.map((d) => {
                const data = maybeBackfillUserCode(COLLECTIONS.INSTRUCTORS, d.id, d.data());
                return { id: d.id, ...data };
              });
              setInstructors(
                fetchedList.filter(
                  (v, i, a) => a.findIndex((t) => t.emailOrPhone === v.emailOrPhone) === i
                )
              );
            },
            (err) => {
              showListenerErrorOnce('instructors', err, 'Eğitmen verileri');
              markReadyOnError('instructors');
            }
          )
        );

        pushListener(() =>
          onSnapshot(
            docRef(COLLECTIONS.STUDENTS, authUid),
            (snap) => {
              readyFlags.students = true;
              checkLiveReady();
              if (!snap.exists()) {
                setStudents([]);
                return;
              }
              const data = maybeBackfillUserCode(COLLECTIONS.STUDENTS, snap.id, snap.data());
              // eslint-disable-next-line no-unused-vars
              const { swimmingRecords, weeklyProgram, ...profileData } = data;
              const student = { id: snap.id, ...profileData };
              setStudents([student]);
              syncStudentProfile(student);
            },
            (err) => {
              showListenerErrorOnce('students', err, 'Öğrenci verileri');
              markReadyOnError('students');
            }
          )
        );

        setMySubData((prev) => (prev.isLoading ? prev : { ...prev, isLoading: true }));
        pushListener(() =>
          subscribeStudentSubData(authUid, setMySubData, {
            onFirstLoad: true,
            label: 'Kendi',
            onError: (err, name) => showListenerErrorOnce(`sub-${name}`, err, name),
          })
        );
      } else if (loggedInUser.role === 'instructor') {
        // Eğitmen profili — doğrudan UID belgesi (e-posta sorgusu kurallarda reddedilir)
        pushListener(() =>
          onSnapshot(
            docRef(COLLECTIONS.INSTRUCTORS, authUid),
            (snap) => {
              readyFlags.instructors = true;
              checkLiveReady();
              if (!snap.exists()) {
                setInstructors([]);
                return;
              }
              const data = maybeBackfillUserCode(COLLECTIONS.INSTRUCTORS, snap.id, snap.data());
              const instructor = { id: snap.id, ...data };
              setInstructors([instructor]);
              syncInstructorProfile(instructor);
            },
            (err) => {
              showListenerErrorOnce('instructors', err, 'Eğitmen verileri');
              markReadyOnError('instructors');
            }
          )
        );

        // Öğrenciler — instructorId (birincil) + e-posta (eski kayıtlar)
        const studentsBySource = [{}, {}];
        const studentSourceState = [{ ok: false, failed: false }, { ok: false, failed: false }];
        const mapStudentDoc = (d) => {
          const data = maybeBackfillUserCode(COLLECTIONS.STUDENTS, d.id, d.data());
          // eslint-disable-next-line no-unused-vars
          const { swimmingRecords, weeklyProgram, ...profileData } = data;
          return { id: d.id, ...profileData };
        };
        const syncStudentsFromSource = (sourceIndex, snapshot) => {
          const nextSource = {};
          snapshot.docs.forEach((d) => {
            nextSource[d.id] = mapStudentDoc(d);
          });
          studentsBySource[sourceIndex] = nextSource;
          const merged = { ...studentsBySource[0], ...studentsBySource[1] };
          const nextList = dedupeStudentsByEmailPreferUid(Object.values(merged), authUid);
          startTransition(() => {
            setStudents(nextList);
          });
        };
        const markStudentsReady = () => {
          readyFlags.students = true;
          checkLiveReady();
        };

        pushListener(() =>
          onSnapshot(
            query(collectionRef('students'), where('instructorId', '==', authUid)),
            (snapshot) => {
              studentSourceState[0].ok = true;
              syncStudentsFromSource(0, snapshot);
              markStudentsReady();
            },
            (err) => {
              studentSourceState[0].failed = true;
              console.warn('Öğrenci (instructorId) sorgusu:', err);
              if (studentSourceState[1].ok || studentSourceState[1].failed) {
                showListenerErrorOnce('students', err, 'Öğrenci verileri');
                markStudentsReady();
              }
            }
          )
        );

        pushListener(() =>
          onSnapshot(
            query(collectionRef('students'), where('instructorEmail', '==', authEmail)),
            (snapshot) => {
              studentSourceState[1].ok = true;
              syncStudentsFromSource(1, snapshot);
              markStudentsReady();
            },
            (err) => {
              studentSourceState[1].failed = true;
              console.warn('Öğrenci (instructorEmail) yedek sorgusu:', err);
              if (studentSourceState[0].ok || studentSourceState[0].failed) {
                if (!studentSourceState[0].ok) {
                  showListenerErrorOnce('students', err, 'Öğrenci verileri');
                }
                markStudentsReady();
              }
            }
          )
        );
      }
    };

    attachListeners();

    return () => {
      cancelled = true;
      setLiveDataReady?.(false);
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [firebaseUser, firebaseUser?.uid, firebaseUser?.email, loggedInUser?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleStudentIdsKey = useMemo(() => {
    if (loggedInUser?.role !== 'instructor') return '';
    return filterMyStudents(students, loggedInUser)
      .map((s) => s.id)
      .sort()
      .join(',');
  }, [students, loggedInUser]);

  // Öğrenci başına bir dinleyici; liste değişince yalnızca eklenen/çıkan öğrenciler açılıp kapanır.
  // Hepsini yeniden kurmak 200 öğrencide her eklemede yüzlerce okuma ve takılma demekti.
  const programListenersRef = useRef(new Map());
  const pendingProgramsRef = useRef({});
  const programsFlushRafRef = useRef(0);

  useEffect(() => {
    const listeners = programListenersRef.current;
    const pending = pendingProgramsRef.current;
    const ids = scheduleStudentIdsKey ? scheduleStudentIdsKey.split(',').filter(Boolean) : [];
    const activeIds = new Set(ids);

    let removed = false;
    listeners.forEach((unsub, studentId) => {
      if (activeIds.has(studentId)) return;
      unsub();
      listeners.delete(studentId);
      delete pending[studentId];
      removed = true;
    });
    if (!ids.length) {
      setAllStudentsPrograms({});
    } else if (removed) {
      setAllStudentsPrograms((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([studentId]) => activeIds.has(studentId)))
      );
    }

    const flushPendingPrograms = () => {
      programsFlushRafRef.current = 0;
      const batch = { ...pending };
      Object.keys(pending).forEach((k) => delete pending[k]);
      if (Object.keys(batch).length === 0) return;
      startTransition(() => {
        setAllStudentsPrograms((prev) => {
          let changed = false;
          const next = { ...prev };
          Object.entries(batch).forEach(([studentId, progs]) => {
            if (!listeners.has(studentId)) return;
            if (sameProgramList(prev[studentId], progs)) return;
            next[studentId] = progs;
            changed = true;
          });
          return changed ? next : prev;
        });
      });
    };

    ids.forEach((studentId) => {
      if (listeners.has(studentId)) return;
      listeners.set(studentId, onSnapshot(
        studentSubCol(studentId, 'programs'),
        (snap) => {
          pending[studentId] = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
          if (!programsFlushRafRef.current) {
            programsFlushRafRef.current = requestAnimationFrame(flushPendingPrograms);
          }
        },
        (err) => {
          console.error(`Program dinleyici hatası (${studentId}):`, err);
          // Takvim tüm öğrenciler yanıt verene kadar bekler; hatalı dinleyici onu sonsuza dek kilitlemesin
          setAllStudentsPrograms((prev) => (
            prev[studentId] || !listeners.has(studentId) ? prev : { ...prev, [studentId]: [] }
          ));
        }
      ));
    });
  }, [scheduleStudentIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (programsFlushRafRef.current) cancelAnimationFrame(programsFlushRafRef.current);
    programsFlushRafRef.current = 0;
    programListenersRef.current.forEach((unsub) => unsub());
    programListenersRef.current.clear();
  }, []);

  useEffect(() => {
    if (!selectedStudent || loggedInUser?.role !== 'instructor') {
      return undefined;
    }

    const studentId = selectedStudent.id;
    setSelectedStudentSubData((prev) => (prev.isLoading ? prev : { ...prev, isLoading: true }));

    return subscribeStudentSubData(studentId, setSelectedStudentSubData, {
      onFirstLoad: true,
      label: 'Seçili öğrenci',
      onError: (err, name) => console.error(`${name}:`, err),
    });
  }, [selectedStudent?.id, loggedInUser?.role]); // eslint-disable-line react-hooks/exhaustive-deps
};
